import {defineStore} from 'pinia'
import type {CategoryInterface} from "~~/server/types/Categories";
import type {OptionsInterface} from "~~/server/types/ProductOptions";
import type {
  ProductInterface,
    ProductListMiniInterface,
    ProductRate,
    ProductRateRequest
} from "~~/server/types/Products";
import type { PageInterface } from "#server/types/page";

export const useProductStore = defineStore('product_options', {
    state: () => ({
        _options: [] as OptionsInterface[],
        _categories: [] as CategoryInterface[],

        _promise_categories: null as Promise<void> | null,
        _promise_options: null as Promise<void> | null,

        _loaded_categories: false,
        _loaded_options: false,
    }),
    getters: {
        options: s => s._options,
        categories: s => s._categories,
        categories_tree: (state) => {
            if (state._categories===null || state._categories.length === 0) return [];

            const categoryMap = new Map<number, CategoryInterface>();
            const validParents = new Set<number>();

            // Създаваме map с всички категории и събираме валидните parent_id
            state._categories.forEach(category => {
                categoryMap.set(category.id, { ...category, children: [] });
                if (category.parent_id) validParents.add(category.parent_id);
            });

            const tree: CategoryInterface[] = [];

            state._categories.forEach(category => {
                const currentCategory = categoryMap.get(category.id);
                if (!currentCategory) return;

                // Ако parent_id го няма в validParents и не е 0, игнорирай категорията
                if (category.parent_id && category.parent_id !== 0 && !categoryMap.has(category.parent_id)) {
                    return;
                }

                if (category.parent_id && categoryMap.has(category.parent_id)) {
                    categoryMap.get(category.parent_id)!.children!.push(currentCategory);
                } else {
                    tree.push(currentCategory);
                }
            });

            return tree;
        },
    },
    actions: {
        async loadOptions() {
            if (this._loaded_options) return
            if (this._promise_options) return this._promise_options;
            const { $api } = useNuxtApp()

            this._promise_options = (async () => {
                const res = await $api<OptionsInterface[]>('product-options', { method: 'GET' })
                this._options = res ?? []
                this._loaded_options = true
            })()

            try {
                await this._promise_options
            }
            finally {
                this._promise_options = null
            }
        },
        async loadCategories() {
            if (this._loaded_categories) return
            if (this._promise_categories) return this._promise_categories;
            const { $api } = useNuxtApp()

            this._promise_categories = (async () => {
                const res = await $api<CategoryInterface[]>('categories', { method: 'GET' })
                this._categories = res ?? []
                this._loaded_categories = true
            })()

            try {
                await this._promise_categories
            }
            finally {
                this._promise_categories = null
            }
        },
        async loadSingleProduct(slug:string):Promise<ProductInterface> {
            const { $api } = useNuxtApp()
            return await $api<ProductInterface>(`products/${slug}`, { method: 'GET' })
        },
        async loadColors(id: number): Promise<ProductListMiniInterface[]> {
            const { $api } = useNuxtApp();
            return await $api<ProductListMiniInterface[]>("products/by-color", {
                method: "POST",
                body: {id},
            });
        },
        async loadRelated(id: number,cid:number,limit:number): Promise<ProductListMiniInterface[]> {
        const { $api } = useNuxtApp();
        return await $api<ProductListMiniInterface[]>("products/by-related", {
          method: "POST",
          body: {id,cid,limit},
        });
      },
        async loadPages(id: number): Promise<PageInterface[]> {
        const { $api } = useNuxtApp();
        return await $api<PageInterface[]>("products/by-pages", {
          method: "POST",
          body: {id},
        });
      },
        async loadRates(id: number): Promise<ProductRate[]> {
            const { $api } = useNuxtApp();
            return await $api<ProductRate[]>("products/load-rates", {
                method: "POST",
                body: {id},
            });
        },
        async rate(form: ProductRateRequest): Promise<ProductRate> {
            const { $api } = useNuxtApp();
            return await $api<ProductRate>("products/rate", { method: "POST", body: form });
        }

    }

})
